// 행군 막대의 메테오, 그리는 쪽 (채택된 제품 장치, 사용자 2026-10-14 「매우 강함 채택.」)
//
// 규칙과 결정의 출처는 `meteor.ts` 머리에 있다. 이 파일은 카드가 행군 모양일 때(착탄 확산 동안 포함) 행군 막대 칸을 그린다.
//
// ── 칸과 트랙 ─────────────────────────────────────────────────────────────────
//
// 바깥 칸이 진행 막대의 역할과 값(role, aria 값, 시험 표식)을 진다. 그 안의 트랙(4px)과 오렌지 채움은 메테오 전의 막대와 같은 자리와
// 길이다. 칸은 트랙 둘레로 위아래 8px, 좌우로 카드 안쪽 여백(16px) 안 14px 까지 비어 있어 머리와 확산이 잘리지 않는다. 칸 밖으로는 아무것도
// 그리지 않는다. 채움 폭은 1% 칸으로 반올림하지 않는다(아래 `fillPercent`).
//
// ── 카드마다 요소 ─────────────────────────────────────────────────────────────
//
// 행군 중 카드마다 여섯(움직임 줄이기면 하나): 채움 안의 꼬리 두 겹(넓고 옅은 푸른 빛, 가늘고 밝은 흰 심), 메테오 층 안의 빛 훑기 하나,
// 불티 창과 그 안의 띠, 머리 둘레 맥동 하나. 머리는 메테오 층의 가상 요소다. 그 가운데 움직이는 것은 넷(빛 겹, 훑기, 불티 띠, 맥동)이다.
// 착탄 확산은 여섯 겹(되짚는 빛, 충격파 고리, 빛줄기, 파편, 잔광, 섬광)을 담은 상자 하나다.
//
// 꼬리를 채움의 자식으로 둔 까닭: 꼬리의 길이가 채움 길이를 넘지 않게 하려는 것이다(앞 판의 약점, 행군 초반 꼬리가 채움보다 길어 오렌지가
// 묻혔다). 채움 폭의 비율로 길이를 묶으면 진행률을 따로 넘기지 않아도 된다. 넓은 빛 겹은 채움 뒤에 깔려 오렌지 위에 푸른색이 섞이지
// 않는다(앞 판의 약점, 오렌지 위 푸른 꼬리가 회보랏빛으로 탁했다). 그래서 오렌지 선은 오렌지 그대로이고 푸른 빛은 그 위아래로만 번진다.
//
// 움직이는 것은 전부 진짜 자식 요소이고, 움직임은 위치 이동, 크기, 투명도뿐이다. 불티, 훑기, 맥동은 머리 자리를 옮기지 않는 장식 반복이라
// 위치의 출처(진행률 하나)는 그대로다.
//
// 장식을 가상 요소가 아니라 진짜 자식 요소로 둔 까닭(2026-10-14 측정): 메테오 층의 위치는 그릴 때마다 인라인 스타일로 바뀌는데, 가상
// 요소는 그때마다 함께 스타일을 다시 계산해 그 위의 애니메이션이 매 프레임 메인 스레드로 돌았다(추적에서 2초에 623번). 자식
// 요소는 상속되지 않는 위치 값이 바뀌어도 다시 계산되지 않는다. 같은 까닭으로 위치는 상속되는 사용자 정의 속성이 아니라 인라인 이동
// 값으로 준다(`index.css` 맨 위 `@source` 한 줄의 까닭).

import { useState } from 'react'

import { METEOR_BURST_MS, riderShiftPercent } from './meteor.ts'

export interface MeteorGaugeProps {
  /** 움직임 줄이기. 머리와 멈춘 빛 겹 하나만 남고, 다른 장식과 확산이 없다. */
  reduced: boolean
  /** `marchProgress` 값 그대로(0 이상 1 이하). 채움과 머리가 이 값 하나에서 나온다. */
  progress: number
  /** 착탄에서 지난 밀리초. 확산이 보이는 동안만 값이 있다. */
  burstElapsedMs: number | null
  label: string
}

/** 채움 폭(퍼센트). 머리 자리(`riderShiftPercent`)와 같은 값에서 나온다. */
export function fillPercent(progress: number): number {
  return Math.min(1, Math.max(0, progress)) * 100
}

/**
 * 착탄 확산 하나. 처음 그려지는 순간의 경과를 얼려 음수 지연으로 준다.
 *
 * 렌더마다 지연을 다시 주면 브라우저가 이미 흐른 시간 위에 새 지연을 또 얹어 확산이 앞으로 건너뛴다. 그래서 마운트 때 한 번만 정한다.
 * 탭 복귀나 재접속으로 다시 마운트되면 그때의 경과로 다시 정해지므로 처음부터 터지지 않는다.
 *
 * 얼린 지연은 마운트와 애니메이션 시작이 같은 순간일 때만 서버 시각과 맞는다. 둘이 벌어지는 자리(가려진 탭, 감춰진 attack 칸)에서는
 * 처음부터 이 요소를 그리지 않는다(`meteor.ts` 의 `gateBurst`, 2026-10-14 반증 지적).
 */
function MeteorBurst(props: { elapsedMs: number }): React.ReactElement {
  const [frozen] = useState(props.elapsedMs)
  return (
    <span
      aria-hidden="true"
      data-testid="view-card-meteor-burst"
      className="meteor-burst"
      style={{ animationDuration: `${String(METEOR_BURST_MS)}ms`, animationDelay: `${String(-frozen)}ms` }}
    >
      {/* 여섯 겹. 겹마다 시간과 지연을 이 상자의 값에서 그대로 물려받는다(index.css), 그래서 얼린 지연 하나가 여섯 겹을 함께 맞춘다. */}
      <span className="meteor-burst-sweep" />
      <span className="meteor-burst-ring" />
      <span className="meteor-burst-rays" />
      <span className="meteor-burst-debris" />
      <span className="meteor-burst-ember" />
      <span className="meteor-burst-flash" />
    </span>
  )
}

export function MeteorGauge(props: MeteorGaugeProps): React.ReactElement {
  const percent = Math.round(props.progress * 100)
  const bursting = props.burstElapsedMs !== null
  const moving = !props.reduced

  return (
    <span
      data-testid="view-card-progress"
      role="progressbar"
      aria-label={props.label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      data-reduced={String(props.reduced)}
      data-burst={String(bursting)}
      className="meteor-lane col-span-2 col-start-1 row-start-2"
    >
      <span className="meteor-track block h-1 rounded-chip bg-line-edge">
        {/* 채움 폭은 반올림하지 않는다. 1% 칸으로 반올림하면 4분 행군에서 약 2.4초마다 3px 씩 뛰는데, 머리가 그 칸을 따르면 멈췄다 뛰기를
            되풀이하고, 따르지 않으면 채움 끝과 머리가 최대 0.5% 어긋난다. 그래서 채움도 머리와 같은 값을 그대로 쓴다. 낭독기에 주는 값
            (aria 값)만 반올림한 퍼센트다. */}
        <span className="meteor-fill block h-1 rounded-chip bg-accent" style={{ width: `${String(fillPercent(props.progress))}%` }}>
          {/* 꼬리 두 겹. 움직임 줄이기면 넓은 빛 겹 하나만 멈춘 채 남는다. */}
          <span className="meteor-glow" />
          {moving && <span className="meteor-core" />}
        </span>
        {/* 위치는 그릴 때마다 진행률에서 바로 정한다. 보간(transition)을 두지 않는다. 열람 화면은 약 0.1초마다 다시 그리고, 4분 행군에서
            한 번에 약 0.1px 움직여 눈에는 이어져 보인다. 보간을 두면 큰 뜀(retime, 재접속, 탭 복귀)을 따로 가려 끊어야 하고, 그리기마다
            보간을 새로 거는 일이 매 프레임 스타일 재계산을 불렀다(2026-10-14 측정). */}
        <span
          aria-hidden="true"
          data-testid="view-card-meteor"
          className="meteor-rider"
          style={{ transform: `translateX(${String(riderShiftPercent(props.progress))}%)` }}
        >
          {/* 장식 반복. 뒤에 적은 것이 위에 그려지고, 머리(가상 요소)가 맨 위다. */}
          {moving && (
            <>
              <span className="meteor-sweep" />
              <span className="meteor-sparks">
                <span />
              </span>
              <span className="meteor-halo" />
            </>
          )}
        </span>
        {props.burstElapsedMs !== null && <MeteorBurst elapsedMs={props.burstElapsedMs} />}
      </span>
    </span>
  )
}
